import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTvControllers } from '@/lib/api/tvController';
import { useTvControllerGroups } from '@/lib/api/tvControllerGroup';
import { Separator } from '@/components/ui/separator';
import {
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import { columns, TvControllerRow } from './columns';
import { useChannelGuides } from '@/lib/api/channelGuide';
import { guidesByTag, guideStatus } from '@/lib/schema/guideSync';
import { TvController } from '@/lib/schema/tvController';
import TvControllerFilter from './tvControllerFilter';
import { TagTreeSelector } from '@/components/ui/tag-tree-selector';
import { Action, ActionButton } from '../boxes-list/action-button';
import { useMemo, useState } from 'react';
import { createTask, waitforTask } from '@/lib/api/task';
import { CommandDialog } from './commandDialog';
import { toast } from 'sonner';
import { toTagTree, ungroupedTags } from './groupTree';
import { TvControllersTabs } from './tvControllersTabs';

export enum Command {
  IMPORT_CHANNEL_GUIDE = 'IMPORT_CHANNEL_GUIDE',
  CHANGE_VOLUME = 'CHANGE_VOLUME',
  CHANGE_CHANNEL = 'CHANGE_CHANNEL',
  MUTE = 'MUTE',
  UNMUTE = 'UNMUTE',
  POWER_ON = 'POWER_ON',
  POWER_OFF = 'POWER_OFF',
  LOAD_CHANNEL_GUIDE = 'LOAD_CHANNEL_GUIDE',
  REBOOT = 'REBOOT',
  SET_NAME = 'SET_NAME',
  GET_NAME = 'GET_NAME',
  GET_CHANNEL_NUMBER = 'GET_CHANNEL_NUMBER',
  SET_IMAGE = 'SET_IMAGE',
  IMAGE_ON = 'IMAGE_ON',
  IMAGE_OFF = 'IMAGE_OFF',
}

export const actions: Action[] = [
  {
    label: 'Change channel',
    value: Command.CHANGE_CHANNEL,
  },
  {
    label: 'Change volume',
    value: Command.CHANGE_VOLUME,
  },
  {
    label: 'Mute',
    value: Command.MUTE,
  },
  {
    label: 'Unmute',
    value: Command.UNMUTE,
  },
  {
    label: 'Power ON',
    value: Command.POWER_ON,
  },
  {
    label: 'Power OFF',
    value: Command.POWER_OFF,
  },
  {
    label: 'Reboot',
    value: Command.REBOOT,
  },
  {
    label: 'Change image',
    value: Command.SET_IMAGE,
  },
  {
    label: 'Image ON',
    value: Command.IMAGE_ON,
  },
  {
    label: 'Image OFF',
    value: Command.IMAGE_OFF,
  },
];

const myCustomGlobalFilterFn: FilterFn<TvControllerRow> = (
  row: Row<TvControllerRow>,
  _columnId: string,
  filterValue: string
) => {
  const searchableColumnIds = ['name', 'ip', 'macAddress']; // Define the columns to search

  if (!filterValue) {
    return true; // No filter value, show all rows
  }

  const normalizedFilterValue = String(filterValue).toLowerCase();

  // Check if any of the specified columns contain the filter value
  return searchableColumnIds.some((id) => {
    const cellValue = row.getValue(id);
    return String(cellValue).toLowerCase().includes(normalizedFilterValue);
  });
};

export default function TvControllersList() {
  const [values] = useTvControllers();
  const [task, setTask] = useState<any>();

  const [groups] = useTvControllerGroups();

  const tagTree = useMemo(
    () => toTagTree(groups, ungroupedTags(groups, values)),
    [groups, values]
  );

  const [channelGuides] = useChannelGuides();

  // Channel guides are set per parent group; each row shows whether the TV
  // holds the current version of its group's guide.
  const rows = useMemo<TvControllerRow[]>(() => {
    const byTag = guidesByTag(groups);
    const guides = new Map(
      ((channelGuides || []) as { id: string; hash?: string }[]).map((g) => [
        g.id,
        g,
      ])
    );
    return ((values || []) as TvController[]).map((tv) => ({
      ...tv,
      guideState: guideStatus(tv, byTag, guides).state,
    }));
  }, [values, groups, channelGuides]);

  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState({});

  const table = useReactTable({
    data: rows,
    columns,
    getRowId: (row) => row.ip,
    globalFilterFn: myCustomGlobalFilterFn,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      pagination: { pageSize: 20 },
      sorting: [{ id: 'name', desc: false }],
    },
    state: {
      columnVisibility: { tags: false },
      globalFilter,
      rowSelection,
    },
  });

  // Show selected rows that live on OTHER pages at the top so they stay visible;
  // selected rows on the current page stay inline (just highlighted).
  const pageRows = table.getRowModel().rows;
  const pageRowIds = new Set(pageRows.map((row) => row.id));
  const offPageSelected = table
    .getFilteredSelectedRowModel()
    .rows.filter((row) => !pageRowIds.has(row.id));

  const renderRow = (row: Row<TvControllerRow>) => (
    <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
      {row.getVisibleCells().map((cell) => (
        <TableCell key={cell.id}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  );
  return (
    <div className="w-full pb-4">
      <div className="flex justify-between items-center">
        <div className="flex flex-col gap-4">
          <h1 className="text-3xl font-bold">ZeeVee Dashboard</h1>
          <TvControllersTabs value="list" />
        </div>
        <div className="flex items-center gap-2">
          <ActionButton
            disabled={table.getSelectedRowModel().rows.length === 0}
            actions={actions}
            onActionSelected={(action: Action) => {
              const task = {
                boxes: table
                  .getSelectedRowModel()
                  .rows.map((box) => box.getValue('ip') as string),
                command: action.value as any,
                isTvControllerTask: true,
              };

              if (
                [
                  Command.CHANGE_VOLUME,
                  Command.CHANGE_CHANNEL,
                  Command.SET_NAME,
                  Command.SET_IMAGE,
                ].includes(action.value as Command)
              ) {
                setTask(task);
                return;
              }

              createTask(task).then((id) => {
                toast.promise(waitforTask(id), {
                  loading: 'Executing task...',
                  success: () => {
                    return 'Task executed successfully';
                  },
                  error: () => {
                    return 'Error executing task';
                  },
                });
              });
            }}
          />

          <Link to="/tv-controllers/create" className="w-fit">
            <Button type="button">New</Button>
          </Link>
        </div>
      </div>
      <CommandDialog
        command={task?.command}
        open={!!task}
        onChancel={() => setTask(undefined)}
        onSubmit={(data) => {
          if (!task) {
            return;
          }
          setTask(undefined);
          createTask({
            ...(task as any),
            data,
          }).then((id) => {
            toast.promise(waitforTask(id), {
              loading: 'Executing task...',
              success: () => {
                return 'Task executed successfully';
              },
              error: () => {
                return 'Error executing task';
              },
            });
          });
        }}
      />

      <Separator className="my-6" />

      <div className="flex items-start gap-6">
        <aside className="w-72 shrink-0">
          <TagTreeSelector
            title="Tags"
            tree={tagTree}
            value={
              (table.getColumn('tags')?.getFilterValue() as string[]) ?? []
            }
            onChange={(values) =>
              table
                .getColumn('tags')
                ?.setFilterValue(values.length ? values : undefined)
            }
          />
        </aside>

        <div className="min-w-0 flex-1">
          <TvControllerFilter table={table} />
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {offPageSelected.length || pageRows.length ? (
                  <>
                    {offPageSelected.map(renderRow)}
                    {pageRows.map(renderRow)}
                  </>
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between gap-4 py-4">
            <div className="flex-1 text-sm text-muted-foreground">
              {table.getSelectedRowModel().rows.length} of{' '}
              {table.getPreFilteredRowModel().rows.length} row(s) selected.
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">Rows per page</p>
                <Select
                  value={`${table.getState().pagination.pageSize}`}
                  onValueChange={(value) => table.setPageSize(Number(value))}
                >
                  <SelectTrigger className="h-8 w-[80px]">
                    <SelectValue
                      placeholder={`${table.getState().pagination.pageSize}`}
                    />
                  </SelectTrigger>
                  <SelectContent side="top">
                    {[20, 50, 100, 200].map((pageSize) => (
                      <SelectItem key={pageSize} value={`${pageSize}`}>
                        {pageSize}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="text-sm font-medium">
                Page {table.getState().pagination.pageIndex + 1} of{' '}
                {table.getPageCount() || 1}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
